import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from '../shell/Icon.tsx';
import { CheckBox } from './Field.tsx';
import { Meter, type MeterProps } from './Meter.tsx';
import { matrixTone, type Tone } from './state.ts';
import { unread, unreadLine, type Read } from './states/read.ts';
import { useStanding } from './states/stopped.ts';
import { Unit } from './Unit.tsx';
import styles from './DataTable.module.css';

/* ==========================================================================
   DataTable — the dense list primitive.

   Sticky header, 26px rows, tabular right-aligned numerics, hover = one
   background step, selection = a checkbox column plus an action bar that
   replaces the header without shifting the table. Rows are arrow-navigable.

   ---------------------------------------------------------------------------
   THE COLUMN-WIDTH POLICY — "the columns ARE the panel."

   A dense table's columns must add up to the container. Three ways to get
   that wrong, all of which this console has shipped at some point:

   1. `table-layout: auto` spreads spare width proportionally over every
      column with no ceiling, so the list gets looser the wider the monitor
      and a two-character enum ends up 300px wide.

   2. One flex column absorbing the whole surplus concentrates the void in a
      single place: measured on /fleet/list, the ink gap between the robot id
      and Model reached 954px at 1920 — one blank corridor bridged by a 1px
      rule, and row tracking got *worse* on a bigger monitor.

   3. Parking the surplus in a zero-content trailing filler. The columns then
      stop short — `Last seen` ending at x=1050 inside a panel whose border is
      at x=1900 — while the row rule, the hover step and the error tint keep
      painting to the edge, so a third to a half of the primary data pane is
      highlighted charcoal every time the pointer crosses a row.

   The policy, which is what the reference tables do
   (`foxglove-device-detail-recordings.png`, `foxglove-events-table-property-chips.png`):

       base   = the column's own widest ink + the cell insets   (measured)
       share  = surplus × weight(kind) / Σweights               (distributed)
       cap    = a per-kind ceiling on `share`, so a 6-character
                enum never takes a scanning column's worth of slack
       residual (everything the caps refused) goes to the columns that can
                turn width into ink — a text input, a meter track, prose —
                never to a blank region. A name is not longer because the
                monitor is, so `identity` takes a share and then stops.

   So surplus is spread across *all* the data columns in proportion to how
   much length each shape can actually use, every column's ink lands inside a
   glance of its neighbour's, and the widths sum to the container: the last
   data column's right edge IS the panel's right edge. The only trailing
   column left is a 22px chevron gutter on tables whose rows open something,
   and that is an affordance, not filler.

   The base is *measured*, for two reasons. The practical one: Chrome's fixed
   table layout honours only a bare length or percentage on a `<col>` and
   silently treats `clamp()`, `min()` and `calc()` as `auto`. The real one:
   the only honest floor for a column is its own content, so the resolver
   reads the ink of every cell — a Range over the contents, which reports
   where the text laid out even in a cell the fixed layout is clipping.
   Columns whose content stretches to the cell (a meter track, a text input)
   opt out with `data-stretch` and are the residual's first claimants, because
   for them surplus width is resolution, not void. Below the width where the
   bases stop fitting the table exceeds its wrapper and the panel scrolls
   rather than crushing the identity column into an ellipsis. Re-measured on
   container resize and when the row count changes; never on a value refresh,
   so a 3-second poll cannot make the columns twitch.
   ========================================================================== */

export type Align = 'left' | 'right' | 'center';

/**
 * The shapes this console actually tabulates. Naming one is how a caller says
 * "this cell has a known width" without repeating the number, and it is what
 * lets the primitive — not each view — own the measurement.
 */
export type ColumnKind =
  | 'identity'   /* name + machine id — the widest scanning column           */
  | 'text'       /* free prose that genuinely varies                         */
  | 'id'         /* mono machine string: model, version, sha                 */
  | 'enum'       /* short mono enum: morphology, role, state                 */
  | 'status'     /* dot + word: status, connectivity, liveness               */
  | 'meter'      /* a Meter cell: stretching track + fixed readout           */
  | 'age'        /* relative timestamp — "10m 23s ago"                       */
  | 'time'       /* absolute clock — "00:53:15Z"                             */
  | 'date'       /* ISO date — "2026-08-26"                                  */
  | 'num'        /* one tabular number (+ its unit), right-aligned           */
  | 'action'     /* a single verb button                                     */
  | 'icon';      /* one icon-only control                                    */

/* Base = the width the shape needs before any surplus is handed out, in `ch`
   of the table's own 12px Inter with the cell's insets included. It is only a
   fallback: once there are rows on screen the base is the column's measured
   ink, because the honest floor for a column is its own content. */
const KIND_BASE: Record<ColumnKind, number> = {
  identity: 26, text: 24, id: 17, enum: 12, status: 15,
  meter: 15, age: 14, time: 12, date: 13, num: 10, action: 12, icon: 7,
};

/** Kinds whose cell content has one true width and must never be stretched to
    the column, so the column is right-aligned to the data whatever the caller
    passed. A meter belongs here by construction (a bounded span is a fixed
    span); a number belongs here because §4 says numerals align on their own
    edge. */
const RIGHT_KINDS = new Set<ColumnKind>(['meter', 'num']);

function alignOf<T>(c: Column<T>): Align {
  if (c.align !== undefined) return c.align;
  if (c.kind !== undefined && RIGHT_KINDS.has(c.kind)) return 'right';
  return 'left';
}

/* Weight = this column's share of the panel's surplus width.
   
   Every column that holds a value of a *known* length gets the SAME share,
   and that is the point: a column's slack is the gap the eye has to cross to
   reach the next column, so equal slack is equal gaps, and the row tracks
   left-to-right at one rhythm instead of stepping over one wide corridor and
   six narrow ones. Only three shapes are exempt — prose and free-text inputs,
   which genuinely hold more when they are longer; a meter, whose track is a
   fixed ruler and takes nothing at all; and the two control shapes, which are
   a button and a glyph and are the size they are. */
const KIND_WEIGHT: Record<ColumnKind, number> = {
  /* prose and free-text inputs are the one shape that genuinely uses length. */
  text: 3,
  /* A meter is a fixed 48px track plus a fixed readout: it takes NO share of
     the surplus, because a bounded 0–100% quantity is the same span of pixels
     on every surface in the console. See MeterCell. */
  meter: 0,
  identity: 1, id: 1,
  status: 1, enum: 1, age: 1, time: 1, date: 1, num: 1,
  action: 0.5, icon: 0.2,
};

/* Cap = the most surplus, in px, this shape may absorb before the void it
   opens stops reading as one row. Past it the width goes to a column that can
   turn it into ink. `text` is effectively uncapped: an image reference or a
   note is as long as it is. */
const KIND_CAP: Record<ColumnKind, number> = {
  identity: 120, text: 900, meter: 0, id: 120,
  status: 120, enum: 110, age: 120, time: 110, date: 110, num: 120,
  action: 70, icon: 14,
};

/** Slack a shed column must clear before it comes back, so one sitting on the
    boundary does not flicker in and out as a value gets a glyph longer. */
const RESTORE_MARGIN = 16;

const FALLBACK_BASE = 14;
const FALLBACK_WEIGHT = 1;
const FALLBACK_CAP = 120;
/** A column the caller marked `flex` is a residual claimant and takes a full
    scanning column's weight, whatever its shape. */
const FLEX_WEIGHT_FLOOR = 3;
const SELECT_COL_PX = 26;
/** The chevron gutter on a table whose rows open something. Not filler: it is
    where the row's own affordance appears, at the panel's right edge. */
const GO_COL_PX = 22;

/* The header is set in 11px mono inside 10px insets; 1ch of the table's own
   12px Inter is a hair wider than a mono character, so `length + 3` covers the
   label plus both insets. A column must never be narrower than its own
   header — a truncated header is a column with no name. */
function headerCh(header: ReactNode, sortable: boolean): number {
  if (typeof header !== 'string') return 0;
  return header.length + 3 + (sortable ? 2.5 : 0);
}

const CH = /^([\d.]+)ch$/;

export interface Column<T> {
  key: string;
  header: ReactNode;
  /**
   * The shape of this column's content. Prefer this to `width`: it keeps the
   * measurement in one place, so a change to the status vocabulary re-measures
   * every status column in the console at once.
   */
  kind?: ColumnKind | undefined;
  /**
   * The unit this column's numbers are in — `%`, `m/s`, `°C`, `GiB`.
   * Rendered as a `--text-low` suffix inside the value cell, at the value's
   * size, so the numbers still align on their own edge (THE BAR §4: value and
   * unit are one cell; never a unit column, never a unit in the header).
   * Suppressed on rows whose value is missing, so an em dash never grows a
   * unit it does not have.
   */
  unit?: string | undefined;
  /** Override for a shape the kinds do not cover. `ch` keeps it in the grid. */
  width?: string | undefined;
  /** Raise this column's grow allowance — it is the one that absorbs slack. */
  flex?: boolean | undefined;
  /** Hard ceiling in `ch`, overriding the kind's grow allowance. */
  max?: string | undefined;
  /** @deprecated Renamed to `max`. Still honoured. */
  flexMax?: string | undefined;
  /** @deprecated The base width now comes from `kind`; ignored. */
  flexMin?: string | undefined;
  align?: Align | undefined;
  /**
   * Shed order. A column with a `drop` is secondary: when the container is
   * too narrow to hold every column at its own content width, the table sheds
   * the highest `drop` first, then the next, until the remaining columns fit
   * exactly — and puts them back, widest-priority first, as the container
   * grows. Columns with no `drop` are essential and are never shed.
   *
   * This is what stops a wide monitor from buying void and a narrow one from
   * buying a horizontal scrollbar: the column SET is a function of the width,
   * the column widths are not. Everything shed is on the row's detail page,
   * which is one click away and is where the long tail belongs.
   */
  drop?: number | undefined;
  /** Machine strings: ids, timestamps, coordinates, enums, versions. */
  mono?: boolean | undefined;
  /** Secondary information, set in --text-mid. */
  dim?: boolean | undefined;
  sortable?: boolean | undefined;
  cell: (row: T) => ReactNode;
}

/**
 * A column's width policy: the floor it may not go below, the share of the
 * panel's surplus it takes, and the ceiling on that share.
 */
interface Geom {
  unit: 'ch' | 'px';
  /** In `unit`. Replaced by the measured ink once there are rows. */
  base: number;
  /** Share of the surplus, relative to the other columns. */
  weight: number;
  /** Ceiling on this column's share of the surplus, in px. */
  cap: number;
  /** A caller-declared hard ceiling on the resolved width, in `ch`. */
  maxCh?: number;
  /** May take the residual the caps refused: it converts width into ink. */
  claimant: boolean;
  /** A width the caller pinned: never overridden by the content measurement. */
  pinned?: boolean;
}

const PX = /^([\d.]+)px$/;

/** Shapes that turn extra width into information rather than into void: a
    longer text field holds a longer image reference or a longer note. A name
    does not get longer because the monitor did, so `identity` is NOT here — it
    takes a weighted share and then stops. Neither is `meter`: its track is a
    ruler at a fixed 48px and extra width there would be a wider ruler, not a
    finer one. */
const CLAIMANT_KINDS = new Set<ColumnKind>(['text']);

function geomOf<T>(c: Column<T>): Geom {
  const head = headerCh(c.header, c.sortable === true);

  if (c.width !== undefined) {
    const inPx = PX.exec(c.width);
    if (inPx !== null) {
      const n = Number(inPx[1]);
      return { unit: 'px', base: n, weight: 0, cap: 0, claimant: false, pinned: true };
    }
    const m = CH.exec(c.width);
    const base = Math.max(m !== null ? Number(m[1]) : FALLBACK_BASE, head);
    return { unit: 'ch', base, weight: 0, cap: 0, claimant: false, pinned: true };
  }

  const unitCh = c.unit !== undefined ? c.unit.length + 1 : 0;
  const base = Math.max(
    (c.kind !== undefined ? KIND_BASE[c.kind] : FALLBACK_BASE) + unitCh,
    head,
  );

  let weight = c.kind !== undefined ? KIND_WEIGHT[c.kind] : FALLBACK_WEIGHT;
  let cap = c.kind !== undefined ? KIND_CAP[c.kind] : FALLBACK_CAP;
  const claimant = c.flex === true || (c.kind !== undefined && CLAIMANT_KINDS.has(c.kind));
  if (c.flex === true) {
    weight = Math.max(weight, FLEX_WEIGHT_FLOOR);
    cap = Math.max(cap, KIND_CAP.text);
  }

  /* `max` is a hard ceiling on the resolved width, expressed in `ch`. It is
     the caller saying "this column has a known longest value"; the cap it
     implies is whatever that leaves above the base. */
  const declaredMax = c.max ?? c.flexMax;
  if (declaredMax !== undefined) {
    const m = CH.exec(declaredMax);
    if (m !== null) return { unit: 'ch', base, weight, cap, maxCh: Number(m[1]), claimant: false };
  }
  return { unit: 'ch', base, weight, cap, claimant };
}

/**
 * A couple of pixels over the widest value we measured, so a reading that gets
 * one glyph longer between measurements (a relative age crossing from `9h` to
 * `10h`) does not clip. Cheaper than re-measuring on every poll, which would
 * make the columns twitch.
 */
const INK_SAFETY = 5;

/** How many body rows the ink measurement samples. */
const INK_SAMPLE = 80;

/** Cells whose content sizes itself to the column rather than to its value. */
const STRETCHY = '[data-stretch],input,select,textarea,progress,meter';

interface Ink {
  min: number;
  max: number;
  /** The cell's own horizontal insets, so a width can be derived from ink. */
  inset: number;
  /** The cell's content fills whatever width it is given: an input, a meter
      track, a sparkline. Unmeasurable, and the first claimant on surplus. */
  stretch: boolean;
}

/**
 * The ink each column actually carries, measured off the rendered cells.
 *
 * A Range over a cell's contents reports where the content *laid out*, not
 * where it was painted, so it returns the true width even for a cell the fixed
 * layout is currently clipping — which is exactly what lets a too-narrow column
 * grow and a too-wide one shrink. Cells that stretch to fill (a meter track, a
 * sparkline) mark themselves `data-stretch` and are skipped: measuring them
 * would only ever return the column's current width, so the column could never
 * come back in.
 */
function measureInk(table: HTMLTableElement, cols: number): (Ink | undefined)[] {
  const out: (Ink | undefined)[] = new Array<Ink | undefined>(cols).fill(undefined);
  const head = table.tHead?.rows[0];
  const body = table.tBodies[0];
  if (body === undefined) return out;

  /* +1: every row carries the trailing filler cell. A row that does not (the
     empty line, the error line, the selection action bar) spans the table and
     is not a measurement. */
  const full = cols + 1;
  const rows: HTMLTableRowElement[] = [];
  if (head !== undefined && head.cells.length === full) rows.push(head);
  const headRows = rows.length;
  for (let r = 0; r < body.rows.length && rows.length - headRows < INK_SAMPLE; r++) {
    const row = body.rows[r]!;
    if (row.cells.length === full) rows.push(row);
  }
  if (rows.length === 0) return out;

  const range = document.createRange();
  for (let c = 0; c < cols; c++) {
    let min = Infinity;
    let max = 0;
    let inset = 0;
    let stretched = false;
    let seen = 0;
    for (let r = 0; r < rows.length; r++) {
      const cell = rows[r]!.cells[c];
      if (cell === undefined) continue;
      /* Content that fills whatever it is given cannot be measured: it would
         only ever report the width the column already has, so the column would
         ratchet itself down and never come back. Those cells declare
         themselves — `data-stretch` for our own primitives, and every form
         control, which is stretchy by construction. */
      if (cell.querySelector(STRETCHY) !== null) {
        stretched = true;
        break;
      }
      const cs = getComputedStyle(cell);
      if (inset === 0) inset = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      range.selectNodeContents(cell);
      const w = range.getBoundingClientRect().width;
      seen += 1;
      if (w > max) max = w;
      /* The header is allowed to set the floor but never the ceiling: a short
         label over long values must not pull the column's cap down. */
      if (r >= headRows && w < min) min = w;
    }
    if (stretched) {
      out[c] = { min: 0, max: 0, inset: 0, stretch: true };
      continue;
    }
    if (seen === 0) continue;
    out[c] = { min: min === Infinity ? max : min, max, inset, stretch: false };
  }
  return out;
}

/** A column as the resolver sees it: all four numbers already in px. */
interface Box {
  base: number;
  /** Share of the surplus, relative to the other columns. */
  weight: number;
  /** Ceiling on that share. */
  cap: number;
  /** May take the residual the caps refused. */
  claimant: boolean;
}

/**
 * Hand the panel's surplus width to the columns.
 *
 * Pass 1 gives every column `surplus × weight / Σweights`, clipped at that
 * column's cap and redistributed among the columns still under theirs, so a
 * 6-character enum never takes a scanning column's worth of slack.
 *
 * Pass 2 hands whatever the caps refused to the claimants — the columns whose
 * content is longer when the column is (an input, a meter track, prose, the
 * identity cell) — with no ceiling, because for them width is resolution. If a
 * table has no claimant at all, the residual is spread evenly over every
 * growable column rather than parked at the edge: an even gap is a rhythm, a
 * single blank region is a hole.
 *
 * The widths therefore sum to `avail` exactly whenever there is surplus to
 * hand out, which is the whole point: the last data column's right edge is the
 * panel's right edge.
 */
function resolveWidths(box: readonly Box[], avail: number): number[] {
  const widths = box.map((g) => g.base);
  let remaining = avail - widths.reduce((a, b) => a + b, 0);
  if (remaining <= 0.5) return widths.map((w) => Math.round(w));

  /* ---- pass 1: weighted shares, clipped at each column's cap ---- */
  let active = box.map((_, i) => i).filter((i) => box[i]!.weight > 0 && box[i]!.cap > 0);
  while (remaining > 0.5 && active.length > 0) {
    const total = active.reduce((a, i) => a + box[i]!.weight, 0);
    if (total <= 0) break;
    let consumed = 0;
    const next: number[] = [];
    for (const i of active) {
      const add = (remaining * box[i]!.weight) / total;
      const room = box[i]!.base + box[i]!.cap - widths[i]!;
      if (add >= room) {
        widths[i] = box[i]!.base + box[i]!.cap;
        consumed += room;
      } else {
        widths[i] = widths[i]! + add;
        consumed += add;
        next.push(i);
      }
    }
    remaining -= consumed;
    if (next.length === active.length) break;
    active = next;
  }

  /* ---- pass 2: the residual, to the columns that turn it into ink ---- */
  if (remaining > 0.5) {
    let claimants = box.map((_, i) => i).filter((i) => box[i]!.claimant);
    if (claimants.length === 0) {
      claimants = box.map((_, i) => i).filter((i) => box[i]!.weight > 0);
    }
    if (claimants.length === 0) claimants = box.map((_, i) => i);
    const total = claimants.reduce((a, i) => a + Math.max(box[i]!.weight, 0.25), 0);
    for (const i of claimants) {
      widths[i] = widths[i]! + (remaining * Math.max(box[i]!.weight, 0.25)) / total;
    }
  }

  /* Round, then put the rounding drift on the widest column so the sum is
     exactly the container and the table never comes up a pixel short of its
     own right edge. */
  const rounded = widths.map((w) => Math.floor(w));
  let drift = Math.round(avail) - rounded.reduce((a, b) => a + b, 0);
  if (drift !== 0) {
    let widest = 0;
    for (let i = 1; i < rounded.length; i++) if (rounded[i]! > rounded[widest]!) widest = i;
    rounded[widest] = rounded[widest]! + drift;
    drift = 0;
  }
  return rounded;
}

/** Is this cell's value actually missing? Then it gets no unit suffix. */
function isBlank(v: ReactNode): boolean {
  if (v === null || v === undefined || v === false) return true;
  if (typeof v === 'string') return v.trim() === '' || v.trim() === '—' || v.trim() === '–';
  return false;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

interface DataTableProps<T> {
  rows: readonly T[];
  columns: readonly Column<T>[];
  rowKey: (row: T) => string;
  density?: 'normal' | 'dense' | 'two' | undefined;

  /**
   * 2px state bar at the row's leading edge.
   *
   * Pass the row's honest semantic tone; the table decides how much of it to
   * spend. A leading-edge bar repeats down every row, so by default it is
   * projected through `matrixTone` — steel unless a human needs to act. A
   * 28-row table whose left edge is a five-hue rainbow has spent all of its
   * colour on the rows that are fine. Set `toneScale="literal"` only for a
   * short, non-repeating list where the category itself is the subject.
   */
  rowTone?: ((row: T) => Tone | null) | undefined;
  toneScale?: 'fleet' | 'literal' | undefined;
  /** Faint tint + a two-pulse arrival flash. Reserve it for faults. */
  rowAlarm?: ((row: T) => boolean) | undefined;

  onRowClick?: ((row: T) => void) | undefined;
  /** The row currently open elsewhere on the screen. */
  currentKey?: string | null | undefined;

  selectable?: boolean | undefined;
  selected?: ReadonlySet<string> | undefined;
  onSelectedChange?: ((next: Set<string>) => void) | undefined;
  /** Verbs shown in place of the header while rows are selected. */
  actions?: ReactNode;

  sort?: SortState | null | undefined;
  onSortChange?: ((next: SortState) => void) | undefined;

  /**
   * One line of text where the first row would be. No illustration.
   *
   * IT IS ONLY USED WHEN THE READ ACTUALLY LANDED. `No readings in this
   * window.` is a claim about the window — it says the platform was asked and
   * answered with nothing — and a table whose source never answered has no
   * standing to make it. Every absence sentence in this console was being
   * printed over dead endpoints for exactly this reason, so the primitive
   * checks before it prints: see the `standing` note in the body.
   */
  empty?: ReactNode | undefined;
  /**
   * The read the rows came from. Hand it over and the absence sentence above
   * is replaced, on this table's OWN evidence, by `Reading…` while the request
   * is in flight and `Nothing has been read.` once it has failed —
   * `unreadLine`'s two words, so no surface invents a third.
   *
   * The single most important case it covers, and the one the console-wide
   * check cannot: `/api/robots/rover-01/telemetry` dying under a perfectly
   * healthy console. `starved` is false, every other panel is answering, and
   * this table has no business saying `No readings in this window.`
   */
  of?: Read | readonly Read[] | undefined;
  error?: string | null | undefined;
  caption?: string | undefined;
}

export function DataTable<T>({
  rows,
  columns: declaredColumns,
  rowKey,
  density = 'normal',
  rowTone,
  toneScale = 'fleet',
  rowAlarm,
  onRowClick,
  currentKey,
  selectable = false,
  selected,
  onSelectedChange,
  actions,
  sort,
  onSortChange,
  empty = 'No rows.',
  of,
  error,
  caption,
}: DataTableProps<T>) {
  /* A ZERO IS A MEASUREMENT. AN UNREAD SOURCE IS NOT — AND NEITHER IS AN
     ABSENCE SENTENCE.

     Every caller of this primitive hands over one line for "there are no
     rows": `No readings in this window.`, `No settled commands in this
     window.`, `No robots match these filters.` Each of them is a claim that
     the platform was ASKED and answered with nothing, and each of them was
     being printed verbatim over an endpoint that had not answered at all —
     which is the same lie as `0 faulted`, in prose.

     `useStanding` already knows the difference and already publishes it: the
     surface's own read (through `ReadBody`, or through an explicit provider)
     or, failing that, the console-wide starvation check. When it says the
     source has STOPPED, its own line wins and the caller's is withheld. When
     it says `reading` or `empty` the caller's line stands — a first read in
     flight must not blank a table that legitimately has no rows, and a read
     that landed empty is exactly what the caller's sentence is for. */
  const standing = useStanding();
  const line =
    of !== undefined && unread(of)
      ? unreadLine(of)
      : standing.reason === 'stopped'
        ? standing.note
        : empty;

  const sel = selected ?? EMPTY_SET;
  const allKeys = rows.map(rowKey);
  const allSelected = rows.length > 0 && allKeys.every((k) => sel.has(k));
  const someSelected = !allSelected && allKeys.some((k) => sel.has(k));

  const toggle = useCallback(
    (key: string) => {
      if (!onSelectedChange) return;
      const next = new Set(sel);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onSelectedChange(next);
    },
    [onSelectedChange, sel],
  );

  /* Roving tabindex: exactly ONE row is in the tab order and the arrows walk
     the rest. A 150-robot fleet must not cost 150 presses of Tab to step past
     the table, and the operator who is navigating by keyboard is navigating
     *within* the list, not through it. */
  const [roved, setRoved] = useState<string | null>(null);
  const firstKey = rows.length > 0 ? rowKey(rows[0]!) : null;
  const currentInRows = currentKey != null && allKeys.includes(currentKey);
  const activeKey =
    roved !== null && allKeys.includes(roved)
      ? roved
      : currentInRows
        ? currentKey
        : firstKey;

  const onKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row);
      return;
    }
    const el = e.currentTarget;
    const body = el.parentElement;
    let target: Element | null = null;
    switch (e.key) {
      case 'ArrowDown': target = el.nextElementSibling; break;
      case 'ArrowUp': target = el.previousElementSibling; break;
      case 'Home': target = body?.firstElementChild ?? null; break;
      case 'End': target = body?.lastElementChild ?? null; break;
      default: return;
    }
    e.preventDefault();
    if (!(target instanceof HTMLElement) || target.tagName !== 'TR') return;
    const key = target.dataset['rowkey'];
    if (key !== undefined) setRoved(key);
    target.focus();
    target.scrollIntoView({ block: 'nearest' });
  };

  /* ---- responsive column set ------------------------------------------
     Which columns are on screen is a function of the container's width; how
     wide each one is, is not. `shed` holds the keys the resolver has taken
     off because they did not fit, and `natRef` remembers what each column
     measured while it was up, so putting one back is a decision made against
     its real ink rather than against an estimate. */
  const [shed, setShed] = useState<ReadonlySet<string>>(EMPTY_SET);
  const natRef = useRef<Record<string, number>>({});
  const columns = useMemo(
    () => (shed.size === 0 ? declaredColumns : declaredColumns.filter((c) => !shed.has(c.key))),
    [declaredColumns, shed],
  );

  /* +1 for the trailing chevron gutter, which is part of every row so the
     rule, the hover step and the tints run to the panel's right edge. It is
     22px on a table whose rows open something and 0 otherwise — never a
     region that exists only to absorb width. */
  const colCount = columns.length + (selectable ? 1 : 0) + 1;
  const hasGo = onRowClick !== undefined;
  const showActions = selectable && actions !== undefined && sel.size > 0;

  /* --- the width policy, resolved (see the note at the top of this file) --- */
  const geomKey = columns
    .map((c) => `${c.key}:${c.kind ?? ''}:${c.width ?? ''}:${c.max ?? c.flexMax ?? ''}:${c.unit ?? ''}:${c.flex === true ? 'f' : ''}:${c.sortable === true ? 's' : ''}:${typeof c.header === 'string' ? c.header : ''}`)
    .join('|');
  const geoms = useMemo<readonly Geom[]>(
    () => [
      ...(selectable
        ? [
            {
              unit: 'px' as const,
              base: SELECT_COL_PX,
              weight: 0,
              cap: 0,
              claimant: false,
              pinned: true,
            },
          ]
        : []),
      ...columns.map(geomOf),
    ],
    // geomKey is the structural signature of `columns`; a new array identity
    // every render must not re-run a layout effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geomKey, selectable],
  );

  const rowCount = rows.length;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const colRefs = useRef<(HTMLTableColElement | null)[]>([]);
  const fillerRef = useRef<HTMLTableColElement | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (wrap === null || table === null) return;
    let lastAvail = -1;

    const apply = (force: boolean) => {
      const avail = wrap.clientWidth;
      if (avail <= 0) return;
      if (!force && avail === lastAvail) return;
      lastAvail = avail;

      /* 1ch of the table's own font. Measured, because the table's font is a
         token and the primitive must not hard-code its advance width. */
      const cs = getComputedStyle(table);
      const probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;width:100ch';
      probe.style.font = `${cs.fontWeight} ${cs.fontSize} / ${cs.lineHeight} ${cs.fontFamily}`;
      wrap.appendChild(probe);
      const chPx = probe.getBoundingClientRect().width / 100 || 7;
      probe.remove();

      const box: Box[] = geoms.map((g) => ({
        base: g.unit === 'px' ? g.base : g.base * chPx,
        weight: g.weight,
        cap: g.pinned === true ? 0 : g.cap,
        claimant: g.claimant,
      }));
      const ink = measureInk(table, geoms.length);
      for (let i = 0; i < box.length; i++) {
        const m = ink[i];
        const g = geoms[i]!;
        if (m === undefined || g.pinned === true || g.unit === 'px') continue;
        if (m.stretch) {
          /* A cell that fills its column cannot be measured, so the declared
             base stands — but it IS the shape that turns surplus into
             resolution, so it becomes a claimant whatever its kind. */
          box[i] = { ...box[i]!, claimant: true, weight: Math.max(g.weight, KIND_WEIGHT.text), cap: Math.max(g.cap, KIND_CAP.text) };
          continue;
        }
        /* The honest floor for a column is its own longest value. */
        const natural = Math.ceil(m.max + m.inset + INK_SAFETY);
        const capPx =
          g.maxCh !== undefined ? Math.max(0, g.maxCh * chPx - natural) : g.cap;
        box[i] = { base: natural, weight: g.weight, cap: capPx, claimant: g.claimant };
      }

      /* The chevron gutter is not part of the distribution: it is a fixed
         affordance at the trailing edge, or nothing at all. */
      const goPx = hasGo ? GO_COL_PX : 0;

      /* ---- which columns fit at all ----------------------------------
         Every visible column has just been measured, so remember what it
         costs. A column that has been on screen is priced at its own ink;
         one that never has is priced at its kind's declared base. */
      const offset = selectable ? 1 : 0;
      for (let i = 0; i < columns.length; i++) {
        const b = box[i + offset];
        if (b !== undefined) natRef.current[columns[i]!.key] = b.base;
      }
      const price = (c: Column<T>): number => {
        const known = natRef.current[c.key];
        if (known !== undefined) return known;
        const g = geomOf(c);
        return g.unit === 'px' ? g.base : g.base * chPx;
      };

      /* Shed the highest `drop` first until the rest fit at their own content
         width. Recomputed from the full declared set every pass, so widening
         the window puts columns back in the reverse order without any special
         case — a shed column just has to clear a small margin on the way in,
         which is what stops one at the boundary from flickering. */
      const nextShed = new Set<string>();
      let need =
        (selectable ? SELECT_COL_PX : 0) +
        goPx +
        declaredColumns.reduce(
          (a, c) => a + price(c) + (shed.has(c.key) ? RESTORE_MARGIN : 0),
          0,
        );
      if (need > avail) {
        const order = declaredColumns
          .filter((c) => c.drop !== undefined)
          .sort((a, b) => (b.drop ?? 0) - (a.drop ?? 0));
        for (const c of order) {
          if (need <= avail) break;
          nextShed.add(c.key);
          need -= price(c) + (shed.has(c.key) ? RESTORE_MARGIN : 0);
        }
      }
      if (nextShed.size !== shed.size || [...nextShed].some((k) => !shed.has(k))) {
        setShed(nextShed);
        return;
      }

      const widths = resolveWidths(box, Math.max(0, avail - goPx));
      widths.forEach((w, i) => {
        const col = colRefs.current[i];
        if (col !== null && col !== undefined) col.style.width = `${w}px`;
      });
      if (fillerRef.current !== null) fillerRef.current.style.width = `${goPx}px`;
      const total = widths.reduce((a, b) => a + b, 0) + goPx;
      table.style.width = `${total}px`;
    };

    apply(true);
    const ro = new ResizeObserver(() => {
      apply(false);
    });
    ro.observe(wrap);
    return () => {
      ro.disconnect();
    };
    // rowCount is a dependency on purpose: the first paint of a table whose
    // data has not arrived can only measure its headers, and the columns must
    // be re-derived the moment there are rows. A value *refresh* does not
    // change the row count, so a 3-second poll never re-lays the columns.
    // `geoms` already changes with the visible set, which is what re-runs
    // this; the rest are read inside `apply`.
  }, [geoms, rowCount, hasGo, shed, declaredColumns, selectable, columns]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <table className={styles.table} data-density={density} ref={tableRef}>
        {caption !== undefined && (
          <caption className={styles.secondary} style={{ captionSide: 'top', textAlign: 'left' }}>
            {caption}
          </caption>
        )}
        <colgroup>
          {selectable && (
            <col
              ref={(el) => {
                colRefs.current[0] = el;
              }}
            />
          )}
          {columns.map((c, i) => (
            <col
              key={c.key}
              ref={(el) => {
                colRefs.current[i + (selectable ? 1 : 0)] = el;
              }}
            />
          ))}
          {/* the chevron gutter: 22px where rows open something, else 0 */}
          <col ref={fillerRef} />
        </colgroup>
        <thead>
          {showActions ? (
            <tr>
              <th className={styles.actionBar} colSpan={colCount}>
                <div className={styles.actionBarInner}>
                  <span className={styles.selCount}>{sel.size} selected</span>
                  {actions}
                </div>
              </th>
            </tr>
          ) : (
            <tr>
              {selectable && (
                <th className={`${styles.th} ${styles.selCell}`} scope="col">
                  <CheckBox
                    aria-label={allSelected ? 'Clear selection' : 'Select all rows'}
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() =>
                      onSelectedChange?.(allSelected ? new Set() : new Set(allKeys))
                    }
                  />
                </th>
              )}
              {columns.map((c) => {
                const sorted = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={styles.th}
                    data-align={alignOf(c)}
                    data-sorted={sorted}
                    aria-sort={
                      sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    {c.sortable === true && onSortChange !== undefined ? (
                      <button
                        type="button"
                        className={styles.thSort}
                        onClick={() =>
                          onSortChange({
                            key: c.key,
                            dir: sorted && sort.dir === 'asc' ? 'desc' : 'asc',
                          })
                        }
                      >
                        {c.header}
                        <span className={styles.sortGlyph} data-on={sorted}>
                          <Icon
                            name={sorted && sort.dir === 'desc' ? 'arrow-down' : 'arrow-up'}
                            size={12}
                          />
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              <th className={`${styles.th} ${styles.gutter}`} scope="col" />
            </tr>
          )}
        </thead>
        <tbody>
          {error != null && (
            <tr>
              <td className={styles.failed} colSpan={colCount}>
                {error}
              </td>
            </tr>
          )}
          {error == null && rows.length === 0 && (
            <tr>
              <td className={styles.empty} colSpan={colCount}>
                {line}
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const key = rowKey(row);
            const raw = rowTone?.(row) ?? null;
            const tone = raw === null ? null : toneScale === 'literal' ? raw : matrixTone(raw);
            const clickable = onRowClick !== undefined;
            return (
              <tr
                key={key}
                className={styles.tr}
                data-tone={tone ?? undefined}
                data-edge={tone !== null}
                data-alarm={rowAlarm?.(row) === true}
                data-selected={sel.has(key)}
                data-current={currentKey === key}
                data-clickable={clickable}
                data-rowkey={key}
                tabIndex={clickable ? (key === activeKey ? 0 : -1) : undefined}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onFocus={clickable ? () => setRoved(key) : undefined}
                onKeyDown={clickable ? (e) => onKeyDown(e, row) : undefined}
              >
                {selectable && (
                  <td className={`${styles.td} ${styles.selCell}`}>
                    <CheckBox
                      aria-label={`Select ${key}`}
                      checked={sel.has(key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(key)}
                    />
                  </td>
                )}
                {columns.map((c) => {
                  const value = c.cell(row);
                  return (
                    <td
                      key={c.key}
                      className={styles.td}
                      data-align={alignOf(c)}
                      data-mono={c.mono === true}
                      data-dim={c.dim === true}
                    >
                      {value}
                      {c.unit !== undefined && !isBlank(value) && (
<Unit unit={c.unit} />
                      )}
                    </td>
                  );
                })}
                <td className={`${styles.td} ${styles.gutter}`}>
                  {clickable && (
                    <span className={styles.rowGo} aria-hidden="true">
                      <Icon name="chevron-right" size={12} />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/**
 * A two-line identity cell: name over its machine id. Use with
 * `density="two"` — 42px, and the id stays selectable and monospaced.
 */
export function IdentityCell({ primary, secondary }: { primary: ReactNode; secondary: ReactNode }) {
  return (
    <>
      <span className={styles.primary}>{primary}</span>
      <span className={styles.secondary}>{secondary}</span>
    </>
  );
}

/**
 * A meter in a table column: the 48px `sm` track, right-aligned in its cell
 * with its readout.
 *
 * The track does not grow with the column and the column does not grow with
 * the monitor. A battery percentage is a bounded quantity, so the same 0–100%
 * has to be the same 48px in every row of every table and in the identity
 * strip on the detail page — which is what makes the published 15% limit tick
 * land at the same distance from the origin everywhere, and what lets an
 * operator find a flat battery by sweeping the column instead of re-reading
 * each bar's proportion. Give the column `kind="meter"`; the alignment comes
 * with the kind.
 */
export function MeterCell(props: MeterProps) {
  return <Meter size="sm" {...props} />;
}
